import React, { useEffect, useRef, useState } from 'react';

import useFetch from '../../react-hooks/use-fetch';
import { stringifyUrl } from '../../utils/url';
import Filters from './filters';
import Alert from '../alert';
import useQueryString from '../../react-hooks/use-query-string';
import Loading from '../loading';
import Pagination from '../pagination';
import Card from './card';

function ProjectListing({
  facets,
  fetchUrl,
  facetsUrl,
  browseTitle,
  clearFilters,
  searchTitle,
  mobileHideFilters,
  mobileShowFilters,
  goToPageLabel,
  paginationLabel,
  searchBtnLabel,
  sortLabel,
  degreeLabel,
  loadingDataLabel,
  errorHeading,
  errorDescription,
  pageQueryString,
  pageSizeQueryString,
  sortQueryString,
  degreeQueryString,
  programsQueryString,
  schoolsQueryString,
  areasQueryString,
  searchQueryString,
  filterQueryString,
  fixedQueryString,
  projectsQueryString,
  relatedpagesQueryString,
  pageSizeInitial,
  sortInitial,
  degreeInitial,
  programsInitial,
  schoolsInitial,
  areaInitial,
  filterInitial,
  fixedInitial,
  projectsInitial,
  relatedpagesInitial,
  sortOptions,
  degreeOptions,
  optionalTitle,
  noResults
}) {
  const containerRef = useRef(null);
  const [firstTime, setFirstTime] = useState(true);
  const [facetsLoaded, setFacetsLoaded] = useState(facets ? false : true);
  const [pageSizeQs, setPageSizeQs] = useQueryString(
    pageSizeQueryString,
    pageSizeInitial
  );
  const [programQs] = useQueryString(
    programsQueryString,
    programsInitial && programsInitial.split('$')
  );
  const [schoolQs] = useQueryString(
    schoolsQueryString,
    schoolsInitial && schoolsInitial.split('$')
  );
  const [filterQs] = useQueryString(filterQueryString, filterInitial);
  const [areasQs, setAreasQs] = useQueryString(
    areasQueryString,
    areaInitial && areaInitial.split('$')
  );
  const [fixedQs] = useQueryString(fixedQueryString, fixedInitial);
  const [relatedPagesQs] = useQueryString(
    relatedpagesQueryString,
    relatedpagesInitial
  );
  const [projectsQs] = useQueryString(projectsQueryString, projectsInitial);
  const [searchQs, setSearchQs] = useQueryString(searchQueryString);
  const [pageQs, setPageQs] = useQueryString(pageQueryString);
  const [sortQs, setSortQs] = useQueryString(sortQueryString, sortInitial);
  const [degreeQs, setDegreeQs] = useQueryString(
    degreeQueryString,
    degreeInitial
  );

  const fetchUrlWithParams = stringifyUrl({
    url: fetchUrl,
    query: {
      ...(pageQs && { [pageQueryString]: pageQs }),
      ...(pageSizeQs && { [pageSizeQueryString]: pageSizeQs }),
      ...(sortQs && { [sortQueryString]: sortQs }),
      ...(degreeQs && { [degreeQueryString]: degreeQs }),
      ...(programQs && { [programsQueryString]: programQs }),
      ...(schoolQs && { [schoolsQueryString]: schoolQs }),
      ...(areasQs && { [areasQueryString]: areasQs }),
      ...(filterQs && { [filterQueryString]: filterQs }),
      ...(searchQs && { [searchQueryString]: searchQs }),
      ...(fixedQs && { [fixedQueryString]: fixedQs }),
      ...(projectsQs && { [projectsQueryString]: projectsQs }),
      ...(relatedPagesQs && { [relatedpagesQueryString]: relatedPagesQs })
    }
  });

  const { response, error, isLoading } = useFetch(fetchUrlWithParams);

  const setQueryString = (queryString, value) => {
    switch (queryString) {
      case 'sort':
        setSortQs(value);
        setPageQs(1);
        break;
      case 'degree':
        setDegreeQs(value);
        setPageQs(1);
        break;
      case 'facets':
        setAreasQs(value);
        setPageQs(1);
        break;
      case 'page':
        setPageQs(value);
        break;
    }
    if (firstTime) setFirstTime(false);
  };

  useEffect(() => {
    if (response && !firstTime) {
      // wait until container is painted to scroll to top
      window.requestAnimationFrame(() =>
        containerRef?.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        })
      );
    }
  }, [response, containerRef, firstTime]);

  if (error) {
    return (
      <Alert
        heading={errorHeading}
        description={errorDescription}
        type="error"
      />
    );
  }

  return (
    <>
      {facets && (
        <Filters
          facetsUrl={facetsUrl}
          browseTitle={browseTitle}
          clearFilters={clearFilters}
          searchTitle={searchTitle}
          searchBtnLabel={searchBtnLabel}
          loadingDataLabel={loadingDataLabel}
          errorHeading={errorHeading}
          errorDescription={errorDescription}
          setSearchQs={setSearchQs}
          setFacetsQS={setQueryString}
          setFacetsLoaded={setFacetsLoaded}
          mobileShowFilters={mobileShowFilters}
          mobileHideFilters={mobileHideFilters.split('#')}
          totalResults={response?.data.TotalResults}
          facetsInitial={areaInitial}
        />
      )}

      {!facets && optionalTitle && (
        <h2 className="project-listing__heading">{optionalTitle}</h2>
      )}

      {facetsLoaded && (
        <div ref={containerRef}>
          <div className="project-listing__top">
            <span
              className="project-listing__total-results"
              role="alert"
              aria-live="assertive"
            >
              {response?.data.ResultsLabel}
            </span>
            <div className="project-listing__filters">
              <div className="project-listing__filters-select-wrapper">
                <label className="project-listing__filters-label">
                  {degreeLabel}
                </label>
                <select
                  className="project-listing__filters-select"
                  value={degreeQs}
                  onChange={(event) =>
                    setQueryString('degree', event.target.value)
                  }
                >
                  {degreeOptions.split('$').map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
              <div className="project-listing__filters-select-wrapper">
                <label className="project-listing__filters-label">
                  {sortLabel}
                </label>
                <select
                  className="project-listing__filters-select"
                  value={sortQs}
                  onChange={(event) =>
                    setQueryString('sort', event.target.value)
                  }
                >
                  {sortOptions.split('$').map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {isLoading ? (
            <Loading label={loadingDataLabel} />
          ) : (
            <>
              <div className="project-listing__gallery">
                {response?.data?.Results?.length ? (
                  response?.data?.Results?.map((item, index) => (
                    <Card key={index} item={item} />
                  ))
                ) : (
                  <div className="project-listing__no-results">{noResults}</div>
                )}
              </div>
              {response?.data?.TotalPages > 1 && (
                <Pagination
                  currentPage={response.data.CurrentPage}
                  totalPages={response.data.TotalPages}
                  goToPageLabel={goToPageLabel}
                  onPageChange={(page) => {
                    setQueryString('page', page);
                  }}
                  label={paginationLabel}
                />
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}

export default ProjectListing;
